import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Phone, Loader2, CheckCircle2, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "+420 775 659 129";
const WHATSAPP_NUMBER = "420775659129";
const SESSION_KEY = "stk_lenka_session";

/** Build a safe WhatsApp deeplink prefilled with the user's last message context.
 * Never throws — falls back to a clean greeting if nothing to summarise. */
function buildWhatsAppLink(lastUserMessage) {
  const trimmed = (lastUserMessage || "").trim().slice(0, 600);
  const intro = "Dobrý den, píšu vám z webu STK Komfort Znojmo.";
  const body = trimmed
    ? `${intro}\n\n${trimmed}\n\nProsím o ozvání ohledně mého požadavku. Děkuji.`
    : `${intro}\n\nProsím o ozvání ohledně mého požadavku. Děkuji.`;
  try {
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(body)}`;
  } catch {
    return `https://wa.me/${WHATSAPP_NUMBER}`;
  }
}

const QUICK_REPLIES = [
  "Potřebuji STK a emise",
  "Chci evidenční kontrolu",
  "Potřebuji přepis vozidla",
  "Potřebuji převozní značky",
  "Auto neprošlo STK",
  "Chci pojištění",
  "Potřebuji servis",
  "Chci mluvit s týmem",
];

const INTRO_MESSAGE = {
  role: "assistant",
  content:
    "Dobrý den, jsem Lenka, asistentka STK Komfort Znojmo. Pomohu vám s orientací ve službách jako STK, emise, evidenční kontrola, přepis vozidla, plná moc nebo další auto služby ve Znojmě a okolí.\n\nNapište mi prosím, co potřebujete vyřešit, a společně najdeme nejjednodušší postup.\n\nPokud chcete, můžete mi rovnou poslat telefon nebo e-mail a stručně popsat svůj požadavek. Tým STK Komfort Znojmo se vám následně ozve.\n\nMůžete nám také napsat na info.stkkomfort@gmail.com nebo zavolat na +420 775 659 129.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [hideTeaser, setHideTeaser] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem(SESSION_KEY) || null;
    return null;
  });
  const [leadCreated, setLeadCreated] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;

    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setLastUserMessage(trimmed);
    setInput("");
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: trimmed,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(SESSION_KEY, data.session_id); } catch (e) { /* localStorage unavailable */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.lead_created) setLeadCreated(true);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Omlouvám se, právě teď se mi nepodařilo spojit s týmem. Pokud spěcháte, zavolejte prosím přímo Lence: +420 775 659 129.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const resetConversation = () => {
    setMessages([INTRO_MESSAGE]);
    setLeadCreated(false);
    setLastUserMessage("");
    setSessionId(null);
    try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* localStorage unavailable */ }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <div
          data-testid="chat-launcher-wrap"
          className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-[60] flex items-end gap-2"
        >
          {!hideTeaser && (
            <div
              data-testid="chat-teaser"
              className="hidden sm:flex flex-col items-end max-w-[240px] rounded-2xl border border-amber-300/30 bg-[#0A0D17]/95 backdrop-blur-md px-4 py-3 text-right shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)]"
            >
              <button
                type="button"
                onClick={() => setHideTeaser(true)}
                className="self-end text-slate-500 hover:text-white"
                aria-label="Zavřít upoutávku"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">
                Potřebujete poradit?
              </div>
              <div className="mt-0.5 text-sm font-semibold text-white">
                Lenka vám pomůže
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="chat-launcher-btn"
            title="Zeptat se Lenky"
            className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-slate-900 shadow-[0_18px_40px_-8px_rgba(212,175,55,0.55)] transition-transform hover:-translate-y-0.5"
            aria-label="Zeptat se Lenky"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 inline-flex h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0A0D17]" />
          </button>
        </div>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="chat-window"
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[400px] z-[60] flex flex-col rounded-2xl border border-amber-300/25 bg-[#0A0D17] text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden max-h-[85vh] sm:max-h-[640px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-b from-[#0E1730] to-[#0A0D17] px-4 py-3">
            <div className="flex items-center gap-3">
              <span
                className="block h-10 w-10 rounded-xl bg-[#0A0D17] ring-1 ring-amber-300/30"
                style={{
                  backgroundImage: "url(/brand/logo.png)",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "150% auto",
                  backgroundPosition: "50% 18%",
                }}
              />
              <div className="leading-tight">
                <div className="text-sm font-bold text-white">
                  Lenka — auto asistentka STK Komfort™
                </div>
                <div className="text-[11px] text-emerald-300 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  online
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 hover:bg-white/5 hover:text-white"
              aria-label="Zavřít chat"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </div>

          {/* Subheader / legal */}
          <div className="px-4 py-2.5 text-[11px] text-slate-400 border-b border-white/5 bg-white/[0.02]">
            Pomůžeme vám se STK, emisemi, přepisy, pojištěním i servisem. STK Komfort™ není stanice STK — STK a emise zajišťujeme přes partnera <span className="text-amber-300/90">PSOTA Znojmo</span>. Pokud spěcháte, volejte <a href="tel:+420775659129" className="text-amber-300">+420 775 659 129</a> nebo pište na <a href="mailto:info.stkkomfort@gmail.com" className="text-amber-300">info.stkkomfort@gmail.com</a>. Odesláním zprávy souhlasíte se zpracováním osobních údajů dle <a href="/ochrana-osobnich-udaju" target="_blank" rel="noreferrer noopener" className="text-amber-300 underline underline-offset-2">Zásad ochrany osobních údajů</a>.
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0A0D17]"
            style={{ minHeight: "200px" }}
          >
            {messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} content={m.content} />
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-slate-400 px-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Lenka píše…
              </div>
            )}
            {leadCreated && (
              <div
                data-testid="chat-lead-confirmation"
                className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2.5 text-xs text-emerald-200"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Poptávka odeslána. Náš tým se vám ozve co nejdříve.
                </span>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {messages.length <= 1 && !loading && (
            <div
              data-testid="chat-quick-replies"
              className="px-4 pt-2 pb-3 border-t border-white/5 bg-white/[0.02]"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/80 mb-2">
                Rychlé volby
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    data-testid={`chat-quick-${q}`}
                    className="rounded-full border border-amber-300/20 bg-amber-300/5 px-3 py-1.5 text-[11px] font-medium text-amber-100 hover:bg-amber-300/15 hover:border-amber-300/40 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Phone CTA + input */}
          <div className="border-t border-white/10 bg-[#06080F] p-3">
            <a
              href={`tel:${PHONE.replace(/\s+/g, "")}`}
              data-testid="chat-call-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 mb-2 transition-all"
            >
              <Phone className="h-4 w-4" />
              Zavolat: {PHONE}
            </a>

            {lastUserMessage && (
              <a
                href={buildWhatsAppLink(lastUserMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-whatsapp-btn"
                className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] px-4 py-2.5 text-sm font-bold text-white mb-2.5 transition-all"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.97L2 22l4.25-1.11c1.75.96 3.73 1.47 5.79 1.47h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.83 9.83 0 0 0 12.04 2zm4.49 11.94c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.22-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.15.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.49-.4-.42-.55-.43-.14-.01-.31-.01-.47-.01a.9.9 0 0 0-.65.31c-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.55.12.16 1.74 2.66 4.22 3.73.59.25 1.05.41 1.41.52.59.19 1.13.16 1.55.1.47-.07 1.46-.6 1.66-1.18.21-.58.21-1.07.14-1.18-.06-.12-.22-.18-.47-.31z"/>
                </svg>
                Pokračovat na WhatsApp
              </a>
            )}

            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Napište zprávu Lence…"
                disabled={loading}
                data-testid="chat-input"
                className="flex-1 rounded-xl border border-white/15 bg-[#0A0D17] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send-btn"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-300 text-slate-900 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Odeslat"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            {messages.length > 1 && (
              <button
                type="button"
                onClick={resetConversation}
                data-testid="chat-reset-btn"
                className="mt-2 w-full text-center text-[11px] text-slate-500 hover:text-amber-300"
              >
                Začít novou konverzaci
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={[
        "flex",
        isUser ? "justify-end" : "justify-start",
      ].join(" ")}
      data-testid={`chat-bubble-${role}`}
    >
      <div
        className={[
          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words",
          isUser
            ? "bg-amber-300 text-slate-900 rounded-br-md"
            : "bg-white/[0.06] text-slate-100 border border-white/10 rounded-bl-md",
        ].join(" ")}
      >
        {content}
      </div>
    </div>
  );
}
